import { useState, useEffect } from 'react';
import apiClient from '../../lib/api';
import HeroSliderManager from './components/HeroSliderManager';
import ServicesManager from './components/ServicesManager';
import OffersManager from './components/OffersManager';
import TestimonialsManager from './components/TestimonialsManager';
import AboutManager from './components/AboutManager';
import GalleryManager from './components/GalleryManager';
import ContactManager from './components/ContactManager';
import BookingManager from './components/BookingManager';
import ServicesPageManager from './components/ServicesPageManager';
import GalleryPageManager from './components/GalleryPageManager';
import BookingPageManager from './components/BookingPageManager';
import PaymentSettingsManager from './components/PaymentSettingsManager';
import ForgotPasswordModal from '../../components/feature/ForgotPasswordModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface Permission {
  id: string;
  name?: string;
  description?: string;
  enabled: boolean;
  parentId?: string;
}

const AdminPage = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('services');
  const [activeSection, setActiveSection] = useState('services');
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  // const [isLocked, setIsLocked] = useState(false); // REMOVED
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // --- Safe permission loader & checker ---
  const loadPermissions = (): Permission[] => {
    try {
      const raw = localStorage.getItem('adminPermissions');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((p) => ({
          id: p.id ?? p.name ?? String(p),
          enabled: !!p.enabled,
          ...(p.parentId && { parentId: p.parentId })
        }));
      }
      return [];
    } catch (err) {
      console.error('loadPermissions parse error:', err);
      return [];
    }
  };

  const hasPermission = (permissionId: string): boolean => {
    return true; // Master Admin removed - always allow
  };

  useEffect(() => {
    checkAuth();
    const perms = loadPermissions();
    setPermissions(perms);

    // const homePermission = perms.find(p => p.id === 'home');
    // if (homePermission && !homePermission.enabled) {
    //   setIsLocked(true);
    // }
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData.role === 'admin' || userData.role === 'staff') {
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          // Not an admin, clear auth
          apiClient.logout();
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        apiClient.logout();
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // if (isLocked) {
    //   setError('Admin access has been restricted by Master Admin');
    //   return;
    // }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.login(credentials.email, credentials.password);

      if (response.user.role !== 'admin' && response.user.role !== 'staff') {
        throw new Error('Access denied. Admin privileges required.');
      }

      setUser(response.user);
      setIsAuthenticated(true);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    apiClient.logout();
    setUser(null);
    setIsAuthenticated(false);
    setActiveTab('services');
    setActiveSection('services');
  };

  // if (isLocked) { ... } REMOVED

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen luxury-gradient flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-luxury p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
            <p className="text-gray-600">Access your salon dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email</label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="Enter your email"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="Enter password"
                required
                disabled={loading}
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-400 hover:bg-primary-500 text-black py-3 rounded-lg font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>


            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-primary-400 hover:text-primary-500 text-sm cursor-pointer transition-colors duration-300"
                disabled={loading}
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </div>

        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
        // adminType="admin" // REMOVED PROP
        />
      </div>
    );
  }

  function PermissionDenied() {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-forbid-line text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">
          You don't have permission to access this section. Contact the Master Admin for access.
        </p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'about':
        return hasPermission('about') ? <AboutManager /> : <PermissionDenied />;
      case 'services-page':
        return hasPermission('services-page') ? <ServicesPageManager /> : <PermissionDenied />;
      case 'services':
        return hasPermission('services') ? <ServicesManager /> : <PermissionDenied />;
      case 'booking-page':
        return hasPermission('booking-page') ? <BookingPageManager /> : <PermissionDenied />;
      case 'booking':
        return hasPermission('booking') ? <BookingManager /> : <PermissionDenied />;
      case 'gallery-page':
        return hasPermission('gallery-page') ? <GalleryPageManager /> : <PermissionDenied />;
      case 'gallery':
        return hasPermission('gallery') ? <GalleryManager /> : <PermissionDenied />;
      case 'contact':
        return hasPermission('contact') ? <ContactManager /> : <PermissionDenied />;
      case 'payment-settings':
        return hasPermission('payment-settings') ? <PaymentSettingsManager /> : <PermissionDenied />;
      case 'offers':
        return hasPermission('home-offers') ? <OffersManager /> : <PermissionDenied />;
      case 'testimonials':
        return hasPermission('home-testimonials') ? <TestimonialsManager /> : <PermissionDenied />;
      case 'hero-slider':
        return (hasPermission('home-hero') || true) ? <HeroSliderManager /> : <PermissionDenied />;
      default:
        return hasPermission('services') ? <ServicesManager /> : <PermissionDenied />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <i className="ri-dashboard-line text-primary-400 mr-3"></i>
              Admin Dashboard
              {user && (
                <span className="ml-4 text-sm font-normal text-gray-600">
                  Welcome, {user.firstName} {user.lastName} ({user.role})
                </span>
              )}
            </h1>
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Navigation</h2>
              <nav className="space-y-2">
                {hasPermission('services') && (
                  <button
                    onClick={() => setActiveSection('services')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'services' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-settings-3-line"></i>
                    <span>Services Management</span>
                  </button>
                )}

                {hasPermission('booking') && (
                  <button
                    onClick={() => setActiveSection('booking')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'booking' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-calendar-check-line"></i>
                    <span>Booking Management</span>
                  </button>
                )}

                {hasPermission('gallery') && (
                  <button
                    onClick={() => setActiveSection('gallery')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'gallery' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-gallery-line"></i>
                    <span>Gallery Management</span>
                  </button>
                )}

                {hasPermission('about') && (
                  <button
                    onClick={() => setActiveSection('about')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'about' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-information-line"></i>
                    <span>About Page</span>
                  </button>
                )}

                {hasPermission('contact') && (
                  <button
                    onClick={() => setActiveSection('contact')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'contact' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-phone-line"></i>
                    <span>Contact Page</span>
                  </button>
                )}

                {hasPermission('offers') && (
                  <button
                    onClick={() => setActiveSection('offers')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'offers' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-price-tag-3-line"></i>
                    <span>Offers</span>
                  </button>
                )}

                {hasPermission('testimonials') && (
                  <button
                    onClick={() => setActiveSection('testimonials')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'testimonials' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-chat-quote-line"></i>
                    <span>Testimonials</span>
                  </button>
                )}
                {(hasPermission('home-hero') || true) && (
                  <button
                    onClick={() => setActiveSection('hero-slider')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeSection === 'hero-slider' ? 'bg-primary-400 text-black' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className="ri-slideshow-3-line"></i>
                    <span>Hero Slider</span>
                  </button>
                )}
              </nav>

              {/* API Status */}
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h3 className="text-sm font-semibold text-green-700 mb-2">
                  <i className="ri-cloud-line mr-1"></i>
                  API Connected
                </h3>
                <p className="text-xs text-green-600">
                  All data is synced with the backend database
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;